import { createSlice, 
         PayloadAction 
       }                                              from "@reduxjs/toolkit";
import { RootState }                                  from "store/Store";
import { ApiErrorInfo }                               from "types";
import { GetSurveyTemplates }                         from "types/apis/surveyInterfaces";
import { APIData, 
         CONTENT_EDIT_STATUS,
         QA_WORKFLOW 
       }                                              from "types/cms/frontendTypes";
import { CMS_DEFAULT_LOCALE }                         from "./cmsPilotSlice";

export interface SurveyTemplateReducerState {
  initDone          : boolean
  error            ?: ApiErrorInfo
  draftTemplates    : APIData<'api::survey-template.survey-template'>[]
  publishedTemplates: APIData<'api::survey-template.survey-template'>[]
  modifiedTemplates : APIData<'api::survey-template.survey-template'>[]
  selectedTemplate  ?: string
  selectedLocale    ?: string
  unsavedTemplates  : Record<string, Partial<APIData<'api::survey-template.survey-template'>>>
  hasUnsavedTemplates: boolean
  reviewWorkflow ?: QA_WORKFLOW
}

const defaultState : SurveyTemplateReducerState = {
  initDone : false,
  error : undefined,
  draftTemplates : [],
  publishedTemplates : [],
  modifiedTemplates : [],
  selectedTemplate : undefined,
  unsavedTemplates : {},
  selectedLocale : CMS_DEFAULT_LOCALE,
  hasUnsavedTemplates : false,
}

export const surveyTemplateSlice = createSlice({
  name : 'surveyTemplate',
  initialState : defaultState,
  reducers : {

    setTemplatesAction : (state : SurveyTemplateReducerState, action : PayloadAction<GetSurveyTemplates.retval>) => {
      state.initDone = true
      state.draftTemplates = action.payload.data.draft
      state.publishedTemplates = action.payload.data.published
      state.modifiedTemplates = action.payload.data.modified
      state.error = undefined

      // create a unique array of all templates and sort it by label  
      const allTemplates = getUniqueSortedTemplates([...state.draftTemplates, ...state.modifiedTemplates, ...state.publishedTemplates])
      state.selectedTemplate = allTemplates[0]?.documentId
    },

    setSelectedLocaleAction: (state: SurveyTemplateReducerState, action: PayloadAction<string>) => {
      state.selectedLocale = action.payload
    },

    setTemplatesErrorAction : (state : SurveyTemplateReducerState, action : PayloadAction<ApiErrorInfo>) => {
      state.error = action.payload
      state.initDone = true
    },

    selectTemplateAction: (state: SurveyTemplateReducerState, action: PayloadAction<string>) => {
      state.selectedTemplate = action.payload
    },

    createTemplateAction: (state: SurveyTemplateReducerState, action: PayloadAction<{
      template : APIData<'api::survey-template.survey-template'>
    }>) => {
      state.draftTemplates.push(action.payload.template)
      state.draftTemplates.sort((a, b) => a.label.localeCompare(b.label))
      state.selectedTemplate = action.payload.template.documentId
    },

    updateTemplatesAction: (state: SurveyTemplateReducerState, action: PayloadAction<{
      templates: APIData<'api::survey-template.survey-template'>[],
    }>) => {
      
      const updatedTemplates = action.payload.templates

      updatedTemplates.forEach((template) => {
        
        const docId = template.documentId

        // Remove from unsaved templates
        if (state.unsavedTemplates[docId]) delete state.unsavedTemplates[docId]

        const editStatus = (template as any).editStatus as CONTENT_EDIT_STATUS
        if (editStatus === CONTENT_EDIT_STATUS.DRAFT) {
          const draftIndex = state.draftTemplates.findIndex(r => r.documentId === docId)
          if (draftIndex !== -1) {
            state.draftTemplates[draftIndex] = template
          } else {
            state.draftTemplates.push(template)
          }
        } else if (editStatus === CONTENT_EDIT_STATUS.MODIFIED) {
          const modifiedIndex = state.modifiedTemplates.findIndex(r => r.documentId === docId)
          if (modifiedIndex !== -1) {
            state.modifiedTemplates[modifiedIndex] = template
          } else {
            state.modifiedTemplates.push(template)
          }
        } 
      })

      if (Object.keys(state.unsavedTemplates).length === 0) {
        state.hasUnsavedTemplates = false;
      }
    },

    deleteTemplatesAction: (state: SurveyTemplateReducerState, action: PayloadAction<string[]>) => {
      const docIds = action.payload
      
      for (const docId of docIds) {
        const draftIndex = state.draftTemplates.findIndex(r => r.documentId === docId)
        if (draftIndex !== -1) {
          state.draftTemplates.splice(draftIndex, 1)
          continue
        }
        
        const modifiedIndex = state.modifiedTemplates.findIndex(r => r.documentId === docId)
        if (modifiedIndex !== -1) {
          state.modifiedTemplates.splice(modifiedIndex, 1)
          continue
        }
        
        const publishedIndex = state.publishedTemplates.findIndex(r => r.documentId === docId)
        if (publishedIndex !== -1) {
          state.publishedTemplates.splice(publishedIndex, 1)
        }
      }

      const allTemplates = getUniqueSortedTemplates([...state.draftTemplates, ...state.modifiedTemplates, ...state.publishedTemplates])
      state.selectedTemplate = allTemplates[0]?.documentId
    },

    publishTemplatesAction: (state: SurveyTemplateReducerState, action: PayloadAction<APIData<'api::survey-template.survey-template'>[]>) => {
      
      const publishedTemplates = action.payload
      
      for (const template of publishedTemplates) {
        const docId = template.documentId

        // Remove from modified or draft
        const modifiedIndex = state.modifiedTemplates.findIndex(r => r.documentId === docId)
        if (modifiedIndex !== -1) {
          state.modifiedTemplates.splice(modifiedIndex, 1)
        } else {
          const draftIndex = state.draftTemplates.findIndex(r => r.documentId === docId)
          if (draftIndex !== -1) {
            state.draftTemplates.splice(draftIndex, 1)
          }
        }

        // Add or update in published
        const existingPublishedIndex = state.publishedTemplates.findIndex(r => r.documentId === docId)
        if (existingPublishedIndex !== -1) {
          state.publishedTemplates[existingPublishedIndex] = template
        } else {
          state.publishedTemplates.push(template)
        }
      }
    },

    unpublishTemplatesAction: (state: SurveyTemplateReducerState, action: PayloadAction<APIData<'api::survey-template.survey-template'>[]>) => {
      const draftTemplates = action.payload

      for (const draftTemplate of draftTemplates) {
        const docId = draftTemplate.documentId
        
        // Remove from published
        const publishedIndex = state.publishedTemplates.findIndex(r => r.documentId === docId)
        if (publishedIndex !== -1) {
          state.publishedTemplates.splice(publishedIndex, 1)
        }

        // Add to draft
        const existingDraftIndex = state.draftTemplates.findIndex(r => r.documentId === docId)
        if (existingDraftIndex === -1) {
          state.draftTemplates.push(draftTemplate)
        }
      }
    },

    discardAllDraftTemplatesAction: (state: SurveyTemplateReducerState, action: PayloadAction<string[]>) => {

      const docIds = action.payload

      for (const docId of docIds) {
        const modifiedIndex = state.modifiedTemplates.findIndex(r => r.documentId === docId)
        if (modifiedIndex !== -1) {
          state.modifiedTemplates.splice(modifiedIndex, 1)
        } else {
          const draftIndex = state.draftTemplates.findIndex(r => r.documentId === docId)
          if (draftIndex !== -1) {
            state.draftTemplates.splice(draftIndex, 1)
          }
          const allTemplates = getUniqueSortedTemplates([...state.draftTemplates, ...state.modifiedTemplates, ...state.publishedTemplates])
          state.selectedTemplate = allTemplates[0]?.documentId    
        }
      }
    },

    resetTemplateState : (state : SurveyTemplateReducerState) => {
      state.initDone = false;
      state.error = undefined;
      state.draftTemplates = [];
      state.publishedTemplates = [];
      state.modifiedTemplates = [];
      state.selectedTemplate = undefined;
      state.unsavedTemplates = {};
      state.hasUnsavedTemplates = false;
    },

    setUnsavedTemplateAction: (state: SurveyTemplateReducerState, action: PayloadAction<{
      documentId: string;
      diff: Partial<APIData<'api::survey-template.survey-template'>>;
    }>) => {
      state.unsavedTemplates[action.payload.documentId] = action.payload.diff;
      state.hasUnsavedTemplates = true;
    },

    discardUnsavedTemplateAction: (state: SurveyTemplateReducerState, action: PayloadAction<string>) => {
      delete state.unsavedTemplates[action.payload];
      if (Object.keys(state.unsavedTemplates).length === 0) {
        state.hasUnsavedTemplates = false;
      }
    },

    setTemplateReviewWorkflowAction: (state: SurveyTemplateReducerState, action: PayloadAction<QA_WORKFLOW>) => {
      state.reviewWorkflow = action.payload;
    }
  }
})

// Helper function to get a unique array of templates and sort it by label
const getUniqueSortedTemplates = (templates: APIData<'api::survey-template.survey-template'>[]) => {
  return templates.filter((template, index, self) =>
    index === self.findIndex((t) => t.documentId === template.documentId)
  ).sort((a, b) => a.label.localeCompare(b.label));
}

export const {
  setTemplatesAction,
  setTemplatesErrorAction,
  createTemplateAction,
  updateTemplatesAction,
  deleteTemplatesAction,
  publishTemplatesAction,
  unpublishTemplatesAction,
  discardAllDraftTemplatesAction,
  resetTemplateState,
  selectTemplateAction,
  setUnsavedTemplateAction,
  discardUnsavedTemplateAction,
  setTemplateReviewWorkflowAction,
  setSelectedLocaleAction
} = surveyTemplateSlice.actions;

export const selectDraftTemplates = (state : RootState) => (state.surveyTemplate as SurveyTemplateReducerState).draftTemplates;
export const selectPublishedTemplates = (state : RootState) => (state.surveyTemplate as SurveyTemplateReducerState).publishedTemplates;
export const selectModifiedTemplates = (state : RootState) => (state.surveyTemplate as SurveyTemplateReducerState).modifiedTemplates;
export const selectTemplatesInitDone = (state : RootState) => (state.surveyTemplate as SurveyTemplateReducerState).initDone;
export const selectTemplatesError = (state : RootState) => (state.surveyTemplate as SurveyTemplateReducerState).error;
export const selectSelectedTemplate = (state : RootState) => (state.surveyTemplate as SurveyTemplateReducerState).selectedTemplate;
export const selectUnsavedTemplates = (state: RootState) => (state.surveyTemplate as SurveyTemplateReducerState).unsavedTemplates;
export const selectHasUnsavedTemplates = (state: RootState) => (state.surveyTemplate as SurveyTemplateReducerState).hasUnsavedTemplates
export const selectReviewWorkflow = (state : RootState) => state.surveyTemplate.reviewWorkflow;
export const selectSelectedLocale = (state : RootState) => (state.surveyTemplate as SurveyTemplateReducerState).selectedLocale;